import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  RadioGroup,
  Radio,
  Stack,
  FormControl,
  FormLabel,
  Divider,
  VStack,
  GridItem,
} from "@chakra-ui/react";
import CustomFileInput from "../../inputs/CustomFileInput";
import { useMediaQuery } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { klaviyoTrackEvent } from "../../../lib/klaviyo";
import { Checkbox } from "@chakra-ui/react";
import ProductDetails from "../../ProductDetails";
import AutocompleteInput from "../../inputs/AutocompleteInput";
import Policy from "../../Policy";
import IndividualForm from "./IndividualForm";
import BusinessForm from "./BusinessForm";
import FormInput from "../../inputs/FormInput";
import SelectInput from "../../inputs/SelectInput";

const SAVForm = () => {
  const [uniqueId] = useState(Date.now() + +(Math.random() * 100).toFixed());
  const [isMobile] = useMediaQuery("(max-width: 480px)");
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [userType, setUserType] = useState(null);
  const [buyOnPictureWebsite, setBuyOnPictureWebsite] = useState(null);
  const [purchaseTypes, setPurchaseTypes] = useState(null);
  const [selectedPurchaseType, setSelectedPurchaseType] = useState({
    code_liste: "",
    label: "",
  });
  const [purchaseSubTypes, setPurchaseSubTypes] = useState(null);
  const [selectedPurchaseSubType, setSelectedPurchaseSubType] = useState({
    code_liste: "",
    label: "",
  });
  const [productSections, setProductSections] = useState(1);
  const [language, setLanguage] = useState("");
  const [livingCountry, setLivingCountry] = useState(null);
  const [purchaseCountry, setPurchaseCountry] = useState(null);
  const [purchaseShop, setPurchaseShop] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const baseUrl = process.env.REACT_APP_SAV_API_BASE_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: Business users must have a codeClient (valid shop selection)
    if (userType === "business" && !purchaseShop) {
      alert(sav_form.client_code_required || "Client code is required");
      return;
    }

    // Validation: Ensure companyName field has a value from selected item
    const formData = new FormData(e.target);
    if (userType === "business" && !formData.get("companyName")) {
      alert(sav_form.company_name_error || "Please select a valid company");
      return;
    }

    const recaptchaToken = await window.grecaptcha.execute(
      process.env.REACT_APP_RECAPTCHA_KEY,
      {
        action: "submit",
      }
    );

    formData.append("g-recaptcha-response", recaptchaToken);

    setIsButtonLoading(true);

    const endpoint = "https://formspree.io/f/xqkrypbd";

    if (!purchaseCountry && livingCountry) {
      formData.set("purchaseCountry", livingCountry.name);
    } else if (purchaseCountry) {
      formData.set("purchaseCountry", purchaseCountry.name);
    }

    formData.set(
      "language",
      sav_form.language.options
        .find((option) => option.label === language)
        .locale.toUpperCase()
    );

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        await klaviyoTrackEvent(new FormData(e.target), "Submitted SAV");
        setIsFormSubmitted(true);
        setIsButtonLoading(false);
      } else {
      }
    } catch (error) {
      console.log(error);
    }
  };

  const { sav_form } = useIntl().messages;

  const locale = window.location.pathname.split("/")[1];

  const isRaccommodeursForm =
    userType &&
    userType === "individual" &&
    livingCountry &&
    livingCountry.economic_area === true;

  const isInternationalBusiness =
    userType &&
    userType === "business" &&
    livingCountry &&
    livingCountry.economic_area === false;

  const isUEBusiness =
    userType &&
    userType === "business" &&
    livingCountry &&
    livingCountry.economic_area === true;

  const showPolicies = userType && livingCountry && !isRaccommodeursForm;

  const handlePurchaseTypeChange = (e) => {
    const selectedOption = purchaseTypes.find(
      (option) => String(option.code_liste) === String(e.target.value)
    );

    if (selectedOption) {
      setSelectedPurchaseType({
        code_liste: selectedOption.code_liste,
        label: selectedOption.commentaire,
      });
    }
  };

  const handlePurchaseSubTypeChange = (e) => {
    const selectedOption = purchaseSubTypes.find(
      (option) => String(option.code_liste) === String(e.target.value)
    );
    if (selectedOption) {
      setSelectedPurchaseSubType({
        code_liste: selectedOption.code_liste,
        label: selectedOption.commentaire,
      });
    }
  };

  const handleUserType = (e) => {
    const value = e.target.value;
    setUserType(value);
  };

  const handleBuyOnPictureWebsite = (e) => {
    const value = e.target.value;
    setBuyOnPictureWebsite(value);
    setShowForm(false);
  };

  const changeLanguage = (e) => {
    const selectedLang = sav_form.language.options.find(
      (option) => option.label === e.target.value
    );

    if (selectedLang) {
      window.location.href = `/${selectedLang.locale}/sav`;
    }
  };

  useEffect(() => {
    const currentLang = window.location.pathname.split("/")[1];
    const selectedLang = sav_form.language.options.find(
      (lang) => lang.locale === currentLang
    );

    if (selectedLang) {
      setLanguage(selectedLang.label);
    }
  }, [sav_form.language.options]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(`${baseUrl}/api/sav_types/${locale}`);

      const data = await response.json();

      setPurchaseTypes(data.result);
    };

    fetchData();
  }, [locale, baseUrl]);

  useEffect(() => {
    if (!selectedPurchaseType.label) return;

    const encodedType = encodeURIComponent(selectedPurchaseType.label);

    const fetchData = async () => {
      const response = await fetch(
        `${baseUrl}/api/sav_subtypes/${locale}/${encodedType}`
      );

      const data = await response.json();

      setPurchaseSubTypes(data.result);
    };

    fetchData();
  }, [locale, selectedPurchaseType.label, baseUrl]);

  return (
    <>
      <Box p={4} className="container">
        <h1>{sav_form.form_title}</h1>
        <p
          className="text-center"
          dangerouslySetInnerHTML={{ __html: sav_form.form_intro }}
        ></p>
        {isFormSubmitted ? (
          <div>
            <h2 style={{ textAlign: "center" }}>
              {sav_form.submission_success.title}
            </h2>
            <p className="text-center">{sav_form.submission_success.message}</p>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <input type="hidden" name="uniqueId" value={uniqueId} />
              <Grid
                templateColumns={{ sm: "repeat(3, 1fr)" }}
                gap={5}
                width="100%"
                pb={3}
              >
                <GridItem colSpan={3}>
                  <SelectInput
                    required
                    name="language"
                    value={language}
                    onChange={changeLanguage}
                    placeholder={sav_form.language.placeholder}
                    options={sav_form.language.options}
                    optionKey={(item) => item.locale}
                    optionValue={(item) => item.label}
                    optionLabel={(item) => item.label}
                  >
                    {sav_form.language.title}
                  </SelectInput>
                </GridItem>
                <GridItem colSpan={3}>
                  <AutocompleteInput
                    name="country"
                    label={sav_form.country.label}
                    placeholder={sav_form.living_country}
                    apiIndex={`pays_${locale}`}
                    selectedItem={livingCountry}
                    setSelectedItem={setLivingCountry}
                    itemLabel={(item) => item?.name}
                    fetchParams={{
                      attributesToSearchOn: ["name"],
                      limit: 100,
                    }}
                    isRequired={true}
                  />
                </GridItem>
                <GridItem colSpan={2}>
                  <RadioGroup value={userType}>
                    {sav_form.identification.title}
                    <Stack spacing={5} direction="row" ml={5}>
                      <Radio
                        colorScheme="red"
                        id="userType-individual"
                        name="userType"
                        value="individual"
                        onChange={handleUserType}
                      >
                        {sav_form.identification.individual}
                      </Radio>
                      <Radio
                        colorScheme="red"
                        id="userType-business"
                        name="userType"
                        value="business"
                        onChange={handleUserType}
                      >
                        {sav_form.identification.business}
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </GridItem>
                {userType && userType === "individual" && livingCountry && (
                  <GridItem colSpan={2}>
                    <RadioGroup value={buyOnPictureWebsite}>
                      {sav_form.buy_on_picture_website.title}
                      <Stack spacing={5} direction="row" ml={5}>
                        <Radio
                          colorScheme="red"
                          id="buyOnPictureWebsite-yes"
                          name="buyOnPictureWebsite"
                          value="yes"
                          onChange={handleBuyOnPictureWebsite}
                        >
                          {sav_form.buy_on_picture_website.yes}
                        </Radio>
                        <Radio
                          colorScheme="red"
                          id="buyOnPictureWebsite-no"
                          name="buyOnPictureWebsite"
                          value="no"
                          onChange={handleBuyOnPictureWebsite}
                        >
                          {sav_form.buy_on_picture_website.no}
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </GridItem>
                )}
              </Grid>

              {buyOnPictureWebsite === "yes" && livingCountry && (
                <input
                  type="hidden"
                  name="purchaseCountry"
                  value={livingCountry.name}
                />
              )}

              {userType === "individual" &&
                !isRaccommodeursForm &&
                buyOnPictureWebsite === "yes" &&
                livingCountry && (
                  <div className="buttons-group">
                    <Button
                      onClick={() => setShowForm(true)}
                      variant="primary"
                      p={4}
                      height={"auto"}
                    >
                      {sav_form.repair_request}
                    </Button>
                    <a
                      className="button"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={
                        getCodeSoc(livingCountry) === (1 || 3 || 6)
                          ? "https://web.global-e.com/Returns/Portal/mZ5y"
                          : "https://web.global-e.com/Returns/Portal/mZ8E"
                      }
                    >
                      {sav_form.return_request}
                    </a>
                  </div>
                )}

              {isRaccommodeursForm && showForm === false ? (
                buyOnPictureWebsite ? (
                  <div className="buttons-group">
                    <a
                      className={`button`}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://${
                        locale !== "fr" ? locale + "." : ""
                      }app.les-raccommodeurs.com/picture-questionnaire`}
                    >
                      {sav_form.repair_request}
                    </a>
                    {livingCountry &&
                      (livingCountry.alpha2code === "FR" ? (
                        buyOnPictureWebsite === "yes" &&
                        userType === "individual" ? (
                          <a
                            className={`button`}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={
                              "https://support.picture-organic-clothing.com/on/demandware.store/Sites-PictureOrganicClothing-Site/fr/Login-Show?rurl=1"
                            }
                          >
                            {sav_form.return_request}
                          </a>
                        ) : null
                      ) : buyOnPictureWebsite === "yes" ? (
                        <a
                          className={`button`}
                          target="_blank"
                          rel="noopener noreferrer"
                          href={"https://web.global-e.com/Returns/Portal/mZ5y"}
                        >
                          {sav_form.return_request}
                        </a>
                      ) : null)}
                  </div>
                ) : null
              ) : (
                <>
                  {userType &&
                    livingCountry &&
                    (showForm ||
                      (!isRaccommodeursForm &&
                        ["CH", "GB", "CA", "US"].includes(
                          livingCountry.alpha2code
                        ) &&
                        buyOnPictureWebsite === "no") ||
                      userType === "business" ||
                      (!isRaccommodeursForm &&
                        !["CH", "GB", "CA", "US"].includes(
                          livingCountry.alpha2code
                        ) &&
                        buyOnPictureWebsite === "no")) && (
                      <>
                        {userType === "individual" && (
                          <IndividualForm
                            locale={locale}
                            purchaseTypes={purchaseTypes}
                            selectedPurchaseType={selectedPurchaseType}
                            handlePurchaseTypeChange={handlePurchaseTypeChange}
                            purchaseSubTypes={purchaseSubTypes}
                            selectedPurchaseSubType={selectedPurchaseSubType}
                            handlePurchaseSubTypeChange={
                              handlePurchaseSubTypeChange
                            }
                            purchaseCountry={purchaseCountry}
                            setPurchaseCountry={setPurchaseCountry}
                            purchaseShop={purchaseShop}
                            setPurchaseShop={setPurchaseShop}
                            buyOnPictureWebsite={buyOnPictureWebsite}
                            livingCountry={livingCountry}
                          />
                        )}
                        {userType === "business" && (
                          <BusinessForm
                            locale={locale}
                            purchaseCountry={purchaseCountry}
                            setPurchaseCountry={setPurchaseCountry}
                            purchaseShop={purchaseShop}
                            setPurchaseShop={setPurchaseShop}
                          />
                        )}
                        <VStack spacing={5} mt={5}>
                          <FormInput
                            required
                            type="date"
                            name="purchaseDate"
                            helper={sav_form.purchase_date.helper}
                          >
                            {sav_form.purchase_date.label}
                          </FormInput>

                          <FormControl isRequired>
                            <FormLabel htmlFor="proofOfPurchase">
                              {sav_form.proof_of_purchase.label}
                            </FormLabel>
                            <CustomFileInput
                              id="proofOfPurchase"
                              name="proofOfPurchase"
                              placeholder={
                                sav_form.proof_of_purchase.placeholder
                              }
                              required
                              helper={sav_form.proof_of_purchase.helper}
                            />
                          </FormControl>
                        </VStack>
                        <Divider mt={6} />
                        <h2>{sav_form.product_details}</h2>
                        {[...Array(productSections)].map((_, index) => (
                          <ProductDetails
                            key={index}
                            index={index}
                            productSections={productSections}
                            setProductSections={setProductSections}
                            userType={userType}
                          />
                        ))}
                        <Divider mt={6} mb={6} />
                        {isInternationalBusiness ? (
                          <FormControl>
                            <RadioGroup defaultValue="0" name="returnChoice">
                              {sav_form.return_choice.label}
                              <Stack spacing={5} direction="row" ml={5}>
                                <Radio
                                  colorScheme="red"
                                  value="Return product"
                                  name="return_product"
                                >
                                  {sav_form.return_choice.return}
                                </Radio>
                                <Radio
                                  colorScheme="red"
                                  value="Repair product"
                                  name="repair_product"
                                >
                                  {sav_form.return_choice.repair}
                                </Radio>
                              </Stack>
                            </RadioGroup>
                          </FormControl>
                        ) : isUEBusiness ? (
                          <input
                            type="hidden"
                            name="return_product"
                            value="Return product"
                          />
                        ) : (
                          <input
                            type="hidden"
                            value="Repair product"
                            name="repair_product"
                          />
                        )}
                        <FormControl isRequired mt={6}>
                          <Checkbox>{sav_form.terms}</Checkbox>
                        </FormControl>
                        <Grid
                          templateColumns={{
                            base: "1fr",
                          }}
                          gap={5}
                          justifyItems={isMobile ? "stretch" : "end"}
                        >
                          <Button
                            type="submit"
                            variant="primary"
                            height="auto"
                            mt="4"
                            mb="4"
                            p="4"
                            width={isMobile ? "100%" : "auto"}
                            isLoading={isButtonLoading}
                          >
                            {sav_form.submit}
                          </Button>
                        </Grid>
                      </>
                    )}
                </>
              )}
            </form>

            {showPolicies && showForm && <Policy />}
            {showForm && <Policy />}
          </>
        )}
      </Box>
    </>
  );
};

export default SAVForm;

function getCodeSoc(country) {
  switch (country.alpha2code) {
    case "US":
      return 2;

    case "CH":
      return 3;

    case "CA":
      return 4;

    case "GB":
      return 6;

    default:
      return 1;
  }
}
